import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Version data structure returned by /version.json
 */
interface VersionData {
  version: string;
  serviceName: string;
  timestamp: string;
}

/**
 * Options for the version check hook
 */
interface UseVersionCheckOptions {
  /**
   * Polling interval in milliseconds.
   * @default 300000 (5 minutes)
   */
  pollInterval?: number;
  
  /**
   * Whether to automatically start polling.
   * @default true
   */
  enabled?: boolean;
  
  /**
   * Callback when a new version is detected.
   */
  onNewVersion?: (serverVersion: VersionData) => void;
}

/**
 * Return type for the version check hook
 */
interface UseVersionCheckReturn {
  /**
   * Whether a new version is available on the server.
   */
  hasNewVersion: boolean;
  
  /**
   * The current version embedded in this build.
   */
  currentVersion: string;
  
  /**
   * The server version data (if fetched).
   */
  serverVersion: VersionData | null;
  
  /**
   * Whether version check is currently loading.
   */
  isLoading: boolean;
  
  /**
   * Error from the last version check attempt.
   */
  error: Error | null;
  
  /**
   * Manually trigger a version check.
   */
  checkNow: () => Promise<void>;
  
  /**
   * Reload the page to get the new version.
   */
  reloadToUpdate: () => void;
}

/**
 * Hook to periodically check for new app versions.
 * 
 * Compares the BUILD_VERSION embedded at build time against the
 * /version.json file served by the server. When a mismatch is detected,
 * it means a new deployment has occurred.
 * 
 * Features:
 * - Polls every 5 minutes by default
 * - Pauses polling when the document is hidden (Page Visibility API)
 * - Immediately checks when the document becomes visible again
 * - Provides a manual check function
 * 
 * @example
 * ```tsx
 * const { hasNewVersion, reloadToUpdate } = useVersionCheck({
 *   onNewVersion: (v) => console.log('New version:', v.version)
 * });
 * 
 * if (hasNewVersion) {
 *   return <Banner onClick={reloadToUpdate}>Update available!</Banner>;
 * }
 * ```
 */
export function useVersionCheck(options: UseVersionCheckOptions = {}): UseVersionCheckReturn {
  const {
    pollInterval = 5 * 60 * 1000, // 5 minutes
    enabled = true,
    onNewVersion
  } = options;

  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [serverVersion, setServerVersion] = useState<VersionData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Get the current build version (embedded at build time)
  const currentVersion = typeof BUILD_VERSION !== 'undefined' ? BUILD_VERSION : 'unknown';
  
  // Track if we've already notified about the new version
  const hasNotifiedRef = useRef(false);
  const onNewVersionRef = useRef(onNewVersion);
  onNewVersionRef.current = onNewVersion;
  
  // Backoff state for rate-limit handling
  const backoffUntilRef = useRef(0);

  /**
   * Fetch /version.json and compare against current version.
   * Respects 429 Retry-After headers to avoid contributing to rate-limit exhaustion.
   */
  const checkVersion = useCallback(async () => {
    if (currentVersion === 'unknown') {
      return;
    }

    // Skip if we're in a backoff period from a previous 429
    if (Date.now() < backoffUntilRef.current) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/version.json?_t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const backoffSeconds = retryAfter ? Math.max(Number(retryAfter), 10) : 60;
        backoffUntilRef.current = Date.now() + backoffSeconds * 1000;
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch version.json: ${response.status}`);
      }

      const data: VersionData = await response.json();
      setServerVersion(data);

      const isNewVersion = data.version !== currentVersion;
      setHasNewVersion(isNewVersion);

      if (isNewVersion && !hasNotifiedRef.current) {
        hasNotifiedRef.current = true;
        onNewVersionRef.current?.(data);
      }
    } catch (err) {
      const fetchError = err instanceof Error ? err : new Error(String(err));
      setError(fetchError);
      if (import.meta.env.PROD) {
        console.warn('[useVersionCheck] Failed to check version:', fetchError.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentVersion]);

  /**
   * Reload the page with cache busting to get the new version.
   */
  const reloadToUpdate = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('_reload', Date.now().toString());
    window.location.replace(url.toString());
  }, []);

  // Set up polling with Page Visibility API integration
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Initial check
    checkVersion();

    // Set up interval for periodic checks
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (intervalId) return;
      intervalId = setInterval(checkVersion, pollInterval);
    };

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    let lastVisibilityCheck = 0;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        const now = Date.now();
        // Throttle: skip check if we checked within the last 30 seconds
        if (now - lastVisibilityCheck > 30_000) {
          lastVisibilityCheck = now;
          checkVersion();
        }
        startPolling();
      }
    };

    // Start polling initially if document is visible
    if (!document.hidden) {
      startPolling();
    }

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, pollInterval, checkVersion]);

  return {
    hasNewVersion,
    currentVersion,
    serverVersion,
    isLoading,
    error,
    checkNow: checkVersion,
    reloadToUpdate
  };
}

export default useVersionCheck;
